import React, { useState, useEffect, useCallback } from 'react';
import { ptzApi } from '../../services/api';
import { useDriverFor } from '../../hooks/useCameraDrivers';
import { useAppStore } from '../../store/appStore';

// Camera audio settings, extracted from PTZController into its own tab. Self-contained:
// loads on mount, edits a draft, saves back to the camera. No shared state with the PTZ pad.
export default function PTZAudioPanel({ camera }) {
  const driver = useDriverFor(camera);
  const { addToast } = useAppStore();
  const [audioSettings, setAudioSettings] = useState(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioSaving, setAudioSaving] = useState(false);
  const [audioDraft, setAudioDraft] = useState(null);

  const loadAudio = useCallback(async () => {
    setAudioLoading(true);
    try {
      const res = await ptzApi.getAudio(camera.id);
      setAudioSettings(res.data);
      setAudioDraft(res.data);
    } catch { setAudioSettings(null); }
    finally { setAudioLoading(false); }
  }, [camera.id]);

  useEffect(() => { loadAudio(); }, [loadAudio]);

  return (
    <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div className="section-title">Audio Settings</div>

      {audioLoading && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>Loading…</div>
      )}
      {!audioLoading && audioSettings === null && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--accent-red)', padding: '12px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)' }}>
          Audio settings not available for this camera.
        </div>
      )}
      {!audioLoading && audioDraft !== null && (
        <>
          {/* Audio Enable */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Audio Input
            </div>
            <button
              onClick={() => setAudioDraft(d => ({ ...d, enabled: !d.enabled }))}
              style={{
                padding: '3px 10px', fontSize: '10px', cursor: 'pointer',
                background: audioDraft.enabled ? 'rgba(0,255,136,0.1)' : 'var(--bg-elevated)',
                border: `1px solid ${audioDraft.enabled ? 'var(--accent-green)' : 'var(--border-dim)'}`,
                borderRadius: 'var(--radius-sm)',
                color: audioDraft.enabled ? 'var(--accent-green)' : 'var(--text-muted)',
                fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.06em',
              }}
            >
              {audioDraft.enabled ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Input Type */}
          {audioDraft.inputType !== undefined && (
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>
                Input Source
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {/* The wire values differ per brand — numeric on some, strings on others —
                    which is a protocol detail the driver owns, not something a panel should
                    carry a lookup table for. */}
                {(driver?.audio.inputs ?? []).map(({ label, value: val }) => (
                  <button key={label}
                    onClick={() => setAudioDraft(d => ({ ...d, inputType: val }))}
                    style={{
                      flex: 1, padding: '4px 6px', cursor: 'pointer',
                      background: audioDraft.inputType === val ? 'rgba(244,123,32,0.15)' : 'var(--bg-elevated)',
                      border: `1px solid ${audioDraft.inputType === val ? 'var(--accent-orange)' : 'var(--border-dim)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: audioDraft.inputType === val ? 'var(--accent-orange)' : 'var(--text-muted)',
                      fontFamily: 'var(--font-mono)', fontSize: '10px',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Input Gain */}
          {audioDraft.volume !== null && audioDraft.volume !== undefined && (
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>
                Input Gain: {audioDraft.volume}%
              </div>
              <input
                type="range" min="0" max="100" value={audioDraft.volume}
                onChange={e => setAudioDraft(d => ({ ...d, volume: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: 'var(--accent-orange)' }}
              />
            </div>
          )}

          {/* Save button */}
          <button
            className="btn btn-primary btn-sm"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={audioSaving}
            onClick={async () => {
              setAudioSaving(true);
              try {
                await ptzApi.setAudio(camera.id, audioDraft);
                setAudioSettings(audioDraft);
                addToast({ type: 'success', message: 'Audio settings saved' });
              } catch (err) {
                addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save audio settings' });
              } finally { setAudioSaving(false); }
            }}
          >
            {audioSaving ? 'Saving...' : 'Save Audio Settings'}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={loadAudio} disabled={audioLoading}>
            Reload from Camera
          </button>
        </>
      )}
    </div>
  );
}
